/**
 * PrismSlot — reusable CSS 3D triangular prism component.
 *
 * Renders the standard 4-layer structure:
 *   1. Clip wrapper  — orientation-aware clip-path
 *   2. Slot          — perspective
 *   3. Compensate    — cancels perspective magnification at translateZ(apothem)
 *   4. Body          — rotates on axis, animated via CSS transition
 *
 * Face children use PrismFace, which reads geometry from context
 * so callers never repeat width/height/transform/backfaceVisibility.
 *
 * Clip-path principle: overflow along the rotation axis, flush on the
 * perpendicular axis. Vert (Y-axis) → inset(-10px 0px). Horiz (X-axis) → inset(0px -10px).
 */

import { createContext, useContext } from 'react';
import type { CSSProperties, ReactNode, MouseEventHandler, TransitionEventHandler } from 'react';
import type { PrismGeometry } from '../utils/prismGeometry';

// ---------------------------------------------------------------------------
// Context — shares geometry from PrismSlot to PrismFace children
// ---------------------------------------------------------------------------

const PrismContext = createContext<PrismGeometry | null>(null);

// ---------------------------------------------------------------------------
// PrismFace
// ---------------------------------------------------------------------------

interface PrismFaceProps {
  angle: number;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  /** Forward any extra HTML div attributes (data-*, aria-*, etc.) */
  [key: string]: unknown;
}

export function PrismFace({ angle, style, children, ...rest }: PrismFaceProps) {
  const g = useContext(PrismContext);
  if (!g) throw new Error('PrismFace must be used inside PrismSlot');
  return (
    <div
      style={{
        position: 'absolute',
        width: g.faceW,
        height: g.faceH,
        transform: g.faceTransform(angle),
        backfaceVisibility: 'hidden',
        ...style,
      }}
      {...(rest as Record<string, unknown>)}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PrismSlot
// ---------------------------------------------------------------------------

interface PrismSlotProps {
  geometry: PrismGeometry;
  /** Current body rotation angle in degrees */
  angle: number;
  /** CSS transition value (e.g. "transform 1000ms ease-in-out"). Omit for no transition. */
  transition?: string;
  /** Fires when the body rotation CSS transition completes (filtered internally). */
  onTransitionEnd?: () => void;
  /** If set, renders a backing rectangle behind the prism with this color. */
  backingColor?: string;
  /** Extra styles on the outermost div (position, zIndex, pointerEvents, etc.). */
  style?: CSSProperties;
  children: ReactNode;
}

export default function PrismSlot({
  geometry: g,
  angle,
  transition,
  onTransitionEnd,
  backingColor,
  style,
  children,
}: PrismSlotProps) {
  // Determine orientation: if faceW equals the face dimension, it's a vertical prism
  const isVert = g.faceW === g.face;
  // Clip: overflow along rotation axis, flush on perpendicular axis
  const clipPath = isVert ? 'inset(-10px 0px)' : 'inset(0px -10px)';

  const handleTransitionEnd: TransitionEventHandler<HTMLDivElement> = (e) => {
    if (e.propertyName === 'transform' && e.target === e.currentTarget) {
      onTransitionEnd?.();
    }
  };

  return (
    <PrismContext.Provider value={g}>
      {/* Outermost — clip + sizing + caller styles */}
      <div style={{ width: g.faceW, height: g.faceH, clipPath, ...style }}>
        {/* Optional backing rectangle — visible behind prism during rotation */}
        {backingColor && (
          <div style={{ position: 'absolute', width: g.faceW, height: g.faceH, backgroundColor: backingColor }} />
        )}
        {/* Slot — perspective */}
        <div style={{ position: 'relative', width: g.faceW, height: g.faceH, perspective: g.perspective }}>
          {/* Compensate — cancels perspective magnification at translateZ(apothem) */}
          <div style={{ width: g.faceW, height: g.faceH, transform: `scale(${g.compensate})`, transformStyle: 'preserve-3d' }}>
            {/* Body — rotates on axis */}
            <div
              style={{
                width: g.faceW,
                height: g.faceH,
                transformStyle: 'preserve-3d',
                transform: g.bodyTransform(angle),
                transition: transition || 'none',
              }}
              onTransitionEnd={onTransitionEnd ? handleTransitionEnd : undefined}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </PrismContext.Provider>
  );
}
